import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from 'twyne-new.ui';

import noTailwindContent from '@/assets/no-tailwind.css?raw';
import { useTheme } from '@/components/Theme';
import { useGlobalTailwindConfig } from '@/components/builder/utils/tailwindConfig/hooks';

import { useInteractionMode } from '../../context';

// import themeContent from './theme.css?raw';

export default function TailwindSandbox({ children, className = 'w-full h-full', bodyClassName = 'w-full h-full', htmlClassName = 'w-full h-full', styles = '', ...props }) {
  const ref = useRef(null);
  const [document, setDocument] = useState(null)
  const { theme: { css: theme }, layers: { base: { css: base }, utilities: { css: utilities } }, tailwindCssConfig: { value: tailwindCssConfig }, font: { config }, keyframe: { css: keyframe } } = useGlobalTailwindConfig()
  const { value: interactionMode } = useInteractionMode()

  const adminTheme = useTheme()

  useEffect(() => {
    if (interactionMode === 'select' && ref.current) {
      ref.current.focus()
    }
  }, [interactionMode])

  useEffect(() => {
    if (document) {
      const handleKeyEvent = event => {
        const clonedEvent = new KeyboardEvent(event.type, event)
        window.dispatchEvent(clonedEvent)
      }

      document.addEventListener('keydown', handleKeyEvent)
      document.addEventListener('keyup', handleKeyEvent)

      return () => {
        document.removeEventListener('keydown', handleKeyEvent)
        document.removeEventListener('keyup', handleKeyEvent)
      }
    }
  }, [document])

  return <>
    <iframe
      ref={ref}
      onLoad={() => setDocument(ref.current.contentDocument)}
      className={cn(className, 'outline-none')}
      srcDoc={`
        <!DOCTYPE html>
        <html class="${cn('overflow-hidden', htmlClassName)}">
          <head>
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script>
            ${styles}
          </head>
          <body class="${cn(adminTheme, bodyClassName)}"></body>
        </html>
      `}
      {...props}
    />
    {document && createPortal(children, document.body)}
    {document && createPortal(<>
      {Object.entries(config || {}).map(([key, url]) => <link key={key} href={url} rel="stylesheet" />)}
    </>, document.head)}
    {document && createPortal(<style type="text/tailwindcss" dangerouslySetInnerHTML={{
      __html: [
        `@import "tailwindcss";
         @custom-variant dark (&:where(.dark, .dark *));`,
        theme ? `@theme {
          ${theme}
        }`: '',
        base ? `@layer base {
          ${base}
        }`: '',
        utilities ? `@layer utilities {
          ${utilities}
        }`: '',
        keyframe,
        noTailwindContent || ''].join('\n'),
    }} />, document.head)}
    {document && createPortal(<style type="text/tailwindcss" dangerouslySetInnerHTML={{
      __html: typeof tailwindCssConfig === 'string' ? tailwindCssConfig : '',
    }} />, document.head)}
  </>
}

export const TailwindSandboxPure = ({ children, styles = '', className = 'w-full h-full overflow-hidden' }) => {
  const { theme: { css: theme }, layers: { base: { css: base }, utilities: { css: utilities } }, tailwindCssConfig: { value: tailwindCssConfig }, font: { config } } = useGlobalTailwindConfig()
  const ref = useRef(null)
  const [document, setDocument] = useState(null)
  const adminTheme = useTheme()

  return <>
    <iframe
      ref={ref}
      onLoad={() => setDocument(ref.current.contentDocument)}
      className={className}
      srcDoc={`
        <!DOCTYPE html>
        <html class='bg-transparent!'>
          <head>
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script>
            ${styles}
          </head>
          <body class="${adminTheme} scrollbar bg-transparent!"></body>
        </html>
      `}
    />
    {document && createPortal(children, document.body)}
    {document && createPortal(<>
      {Object.entries(config || {}).map(([key, url]) => <link key={key} href={url} rel="stylesheet" />)}
    </>, document.head)}
    {document && createPortal(<style type="text/tailwindcss" dangerouslySetInnerHTML={{
      __html: [
        `@import "tailwindcss";
         @custom-variant dark (&:where(.dark, .dark *));`,
        theme ? `@theme { ${theme} }` : '',
        base ? `@layer base { ${base} }` : '',
        utilities ? `@layer utilities { ${utilities} }` : '',
        typeof tailwindCssConfig === 'string' ? clearConfig(tailwindCssConfig) : '',
        `:root .scrollbar {\n  --scrollbar-track-bg: transparent;\n  --scrollbar-thumb-bg: theme('colors.gray.300');\n  --placeholder-color: theme('colors.gray.300')\n}\n\n:root .scrollbar.dark {\n  --scrollbar-track-bg: transparent;\n  --scrollbar-thumb-bg: theme('colors.gray.800');\n  --placeholder-color: theme('colors.gray.800');\n}\n\n.scrollbar ::-webkit-scrollbar {\n  width: .25rem;\n  height: .25rem\n}\n\n.scrollbar ::-webkit-scrollbar-track {\n  background-color: var(--scrollbar-track-bg);\n  border-radius: 100vw;\n  margin-block: 0\n}\n\n.scrollbar ::-webkit-scrollbar-thumb {\n  background-color: var(--scrollbar-thumb-bg);\n  border: 1px solid var(--scrollbar-track-bg);\n  border-radius: 100vw\n}`,
      ].join('\n'),
    }} />, document.head)}
  </>
}

const clearConfig = config => {
  const keysToReplace = ['background-color', 'padding', 'margin']
  keysToReplace.forEach(key => {
    config = config.replaceAll(key, '')
  })
  return config
}
